import React, {useEffect, useMemo, useRef, useState, useCallback} from 'react'
import PropTypes from 'prop-types'
import {findImageGroupBy} from '../../../utils/image-groups-utils'
import {Flex, useBreakpointValue, useMultiStyleConfig} from '@chakra-ui/react'
import GallerySlider from './gallery-slider'
import ProductDetailGallery from './product-detail-gallery'
import {handleChevronStyles, LARGE, SMALL} from './utils'
import {parseAndSanitizeJSON} from '../../../utils/utils'
import YoutubeIframe from '../../youtube-video'
import {useIntl} from 'react-intl'
import {GalleryChevronLeftIcon, GalleryChevronRightIcon} from '../../icons'
import {remCalc} from '../../../theme/_utils'

const messages = {
    previousImage: {
        id: 'gallery.previousImage',
        defaultMessage: 'Previous image',
    },
    nextImage: {
        id: 'gallery.nextImage',
        defaultMessage: 'Next image',
    },
    goToImage: {
        id: 'gallery.goToImage',
        defaultMessage: 'Go to image {index}',
    },
    openGallery: {
        id: 'gallery.openGallery',
        defaultMessage: 'Open full screen gallery',
    },
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * When there are no thumbnail images but a video is present we fall back to
 * showing the video slide first, so return its index.  Otherwise return null
 * to signal that normal index resolution should proceed.
 */
const resolveVideoFallbackIndex = ({thumbnailCount, hasVideo, videoIndex}) =>
    thumbnailCount === 0 && hasVideo ? videoIndex : null

/**
 * Resolve the effective initial slide index, honouring the video fallback.
 */
const resolveInitialSelectedIndex = ({
    initialSelectedIndex,
    thumbnailCount,
    hasVideo,
    videoIndex,
}) => {
    const videoFallback = resolveVideoFallbackIndex({thumbnailCount, hasVideo, videoIndex})
    if (videoFallback !== null) return videoFallback
    return Number.isInteger(initialSelectedIndex) ? initialSelectedIndex : null
}

const getInitialSelectedIndex = (opts) => resolveInitialSelectedIndex(opts) ?? 0

// ---------------------------------------------------------------------------

/**
 * GalleryBody renders the hero stage, thumbnail strip, navigation arrows,
 * pagination dots, and an optional tab-list slot (tabsContent).
 *
 * Slide selection can be driven from the outside by passing `selectedIndex`
 * together with `onSelectedIndexChange`.  When those props are omitted the
 * component manages its own internal index (uncontrolled mode).
 */
const GalleryBody = ({
    imageGroups = [],
    selectedVariationAttributes = {},
    lazy = false,
    openModal,
    variant,
    videoUrl,
    setIsModalGallerySwiper,
    isModalGallerySwiper,
    isModalOpen,
    modalInitValue,
    powerImages = [],
    subModelIdentifier,
    // Controlled slide index.  When provided the parent owns the current position.
    selectedIndex: controlledSelectedIndex,
    onSelectedIndexChange,
    // Used only in uncontrolled mode as the starting slide.
    initialSelectedIndex = 0,
    onBoundaryNavigate,
    onJumpToIndex,
    bucketAnimationDirection,
    paginationTotal,
    paginationOffset = 0,
    tabsContent = null,
    bodyContent = null,
    panelId,
    activeTabId,
}) => {
    const intl = useIntl()
    const styles = useMultiStyleConfig('ImageGallery', {variant})
    const isDesktop = useBreakpointValue({base: false, lg: true}) || false
    const isTablet = useBreakpointValue({base: false, md: true}) || false
    const useVerticalThumbnailAxis = isDesktop && !isModalOpen

    const swiperRef = useRef(null)
    const scrollRef = useRef(null)
    const prevArrowRef = useRef(null)
    const nextArrowRef = useRef(null)
    const sliderRef = useRef(null)
    // Flag used to swallow a navigation event that was already handled by a child.
    const stop = useRef(null)

    const [scrollPosition, setScrollPosition] = useState(0)
    const [isStageHovered, setIsStageHovered] = useState(false)
    const [modalMediaTransitionDirection, setModalMediaTransitionDirection] = useState(null)

    // ---------------------------------------------------------------------------
    // Image groups
    // ---------------------------------------------------------------------------

    // Merge standard product images (LARGE) with any extra power images.
    const heroImageGroup = useMemo(() => {
        const standardGroup = findImageGroupBy(imageGroups, {
            viewType: LARGE,
            selectedVariationAttributes,
        })
        if (powerImages?.length > 0) {
            return {
                images: [
                    ...(standardGroup?.images || []),
                    ...powerImages.map((image, index) => ({
                        ...image,
                        alt: image.alt || image.title || `Power Image ${index + 1}`,
                        disBaseLink: image.imageLink || image.url,
                        link: image.imageLink || image.url,
                        title: image.title || `Power Image ${index + 1}`,
                    })),
                ],
                viewType: LARGE,
            }
        }
        return standardGroup
    }, [selectedVariationAttributes, powerImages, imageGroups])

    // Same merge for thumbnails (SMALL view type).
    const thumbnailImageGroup = useMemo(() => {
        const standardGroup = findImageGroupBy(imageGroups, {
            viewType: SMALL,
            selectedVariationAttributes,
        })
        if (powerImages?.length > 0) {
            return {
                images: [
                    ...(standardGroup?.images || []),
                    ...powerImages.map((image, index) => ({
                        ...image,
                        alt: image.alt || image.title || `Power Image ${index + 1}`,
                        disBaseLink: image.imageLink || image.url,
                        link: image.imageLink || image.url,
                        title: image.title || `Power Image ${index + 1}`,
                    })),
                ],
                viewType: SMALL,
            }
        }
        return standardGroup
    }, [selectedVariationAttributes, powerImages, imageGroups])

    const videos = parseAndSanitizeJSON(videoUrl)?.filter((item) => item?.videolink) || []
    const thumbnailImages = thumbnailImageGroup?.images || []
    // Video slide(s) are always placed after all image slides.
    const videoIndex = thumbnailImages.length
    const slideCount = thumbnailImages.length + videos.length

    // ---------------------------------------------------------------------------
    // Controlled vs uncontrolled slide index
    // ---------------------------------------------------------------------------

    const isControlled = typeof controlledSelectedIndex === 'number'

    const [internalIndex, setInternalIndex] = useState(() =>
        getInitialSelectedIndex({
            initialSelectedIndex,
            thumbnailCount: thumbnailImages.length,
            hasVideo: videos.length > 0,
            videoIndex,
        }),
    )

    // The effective index is always the controlled value when the parent provides one.
    const selectedIndex = isControlled ? controlledSelectedIndex : internalIndex

    // When the parent changes the controlled index (e.g. a bucket tab was clicked)
    // we jump to the target slide instantly (speed = 0) so the user is not forced
    // to watch all intermediate slides scroll past.
    useEffect(() => {
        if (!isControlled) return

        // Instant jump: pass 0 as the speed argument to skip the swipe animation.
        swiperRef.current?.slideTo(controlledSelectedIndex, 0)

        if (scrollRef.current) {
            if (useVerticalThumbnailAxis) {
                scrollRef.current.scrollTop = controlledSelectedIndex * 88
                setScrollPosition(scrollRef.current.scrollTop)
            } else {
                const itemSize = isTablet ? 86 : 66
                scrollRef.current.scrollLeft = controlledSelectedIndex * itemSize
                setScrollPosition(scrollRef.current.scrollLeft)
            }
        }
    }, [controlledSelectedIndex])

    /**
     * Central setter: updates internal state (uncontrolled) and/or notifies the
     * parent (controlled).  All internal navigation must go through this function.
     */
    const updateSelectedIndex = useCallback(
        (idx) => {
            if (!isControlled) setInternalIndex(idx)
            onSelectedIndexChange?.(idx)
        },
        [isControlled, onSelectedIndexChange],
    )

    // ---------------------------------------------------------------------------
    // Derived values
    // ---------------------------------------------------------------------------

    const video = videos[selectedIndex - videoIndex]
    const heroImage =
        heroImageGroup?.images?.[selectedIndex] ||
        (video?.videolink && selectedIndex === videoIndex)
    const isVideoSlide = Boolean(video?.videolink) && selectedIndex === videoIndex
    const isVideoSlideIndex = (index) => index >= videoIndex && index < videoIndex + videos.length
    const modalMediaTransitionSx =
        isModalOpen && modalMediaTransitionDirection
            ? {
                  '@keyframes galleryModalMediaEnterFromNext': {
                      from: {opacity: 0, transform: 'translateX(8%)'},
                      to: {opacity: 1, transform: 'translateX(0)'},
                  },
                  '@keyframes galleryModalMediaEnterFromPrevious': {
                      from: {opacity: 0, transform: 'translateX(-8%)'},
                      to: {opacity: 1, transform: 'translateX(0)'},
                  },
                  animation: `galleryModalMediaEnterFrom${
                      modalMediaTransitionDirection === 'next' ? 'Next' : 'Previous'
                  } 300ms ease-out`,
              }
            : undefined

    const loadingStrategy = lazy ? 'lazy' : 'eager'
    const showVideoStageInModal = isModalOpen && !isModalGallerySwiper && Boolean(video?.videolink)
    const horizontalArrowItemsCount = isModalOpen ? slideCount : thumbnailImages.length
    const prevArrowBoundary = isModalOpen ? -1 : 0
    const nextArrowBoundary = isModalOpen
        ? horizontalArrowItemsCount
        : horizontalArrowItemsCount - 1

    const currentSlideIndex =
        selectedIndex === videoIndex
            ? Math.max(slideCount - 1, 0)
            : Number.isInteger(selectedIndex) && selectedIndex >= 0
            ? Math.min(selectedIndex, Math.max(thumbnailImages.length - 1, 0))
            : 0

    const paginationSlideCount = paginationTotal ?? slideCount
    const paginationActiveIndex = paginationOffset + currentSlideIndex

    // ---------------------------------------------------------------------------
    // Navigation
    // ---------------------------------------------------------------------------

    const stopPropagation = () => {
        stop.current = true
    }

    /**
     * Move one slide forward (move = true) or backward (move = false).
     * When the boundary of the current bucket is reached, delegates to
     * onBoundaryNavigate so the parent can advance to the next bucket.
     */
    const handleGalleryNavigation = (move) => {
        if (stop.current) {
            stop.current = false
            return
        }

        const maxIndex = slideCount - 1

        if (maxIndex < 0) {
            onBoundaryNavigate?.(move)
            return
        }

        const currentIdx = Math.min(Math.max(selectedIndex, 0), maxIndex)
        const atStart = currentIdx <= 0
        const atEnd = currentIdx >= maxIndex

        if ((move && atEnd) || (!move && atStart)) {
            const navigated = onBoundaryNavigate?.(move)
            if (navigated) return
        }

        const targetIdx = move ? (atEnd ? 0 : currentIdx + 1) : atStart ? maxIndex : currentIdx - 1

        if (targetIdx === selectedIndex) return

        if (isModalOpen) {
            if (isVideoSlideIndex(selectedIndex) !== isVideoSlideIndex(targetIdx)) {
                setModalMediaTransitionDirection(move ? 'next' : 'previous')
            }
            setIsModalGallerySwiper(!isVideoSlideIndex(targetIdx))
        }

        updateSelectedIndex(targetIdx)
        swiperRef?.current?.slideTo(targetIdx)

        // Scroll the thumbnail strip to keep the active thumbnail visible.
        if (scrollRef.current) {
            const shouldWrapToStart = move && atEnd
            const shouldWrapToEnd = !move && atStart

            if (useVerticalThumbnailAxis) {
                if (shouldWrapToStart) {
                    scrollRef.current.scrollTop = 0
                } else if (shouldWrapToEnd) {
                    scrollRef.current.scrollTop =
                        scrollRef.current.scrollHeight - scrollRef.current.clientHeight
                } else {
                    scrollRef.current.scrollTop += move ? 88 : -88
                }
                setScrollPosition(scrollRef.current.scrollTop)
            } else {
                if (shouldWrapToStart) {
                    scrollRef.current.scrollLeft = 0
                } else if (shouldWrapToEnd) {
                    scrollRef.current.scrollLeft =
                        scrollRef.current.scrollWidth - scrollRef.current.clientWidth
                } else {
                    const delta = isTablet ? (move ? 86 : -86) : move ? 66 : -66
                    scrollRef.current.scrollLeft += delta
                }
                setScrollPosition(scrollRef.current.scrollLeft)
            }
        }
    }

    /**
     * Jump directly to a specific slide index (e.g. thumbnail click or dot click).
     */
    const goToSlide = (targetIdx) => {
        if (targetIdx === selectedIndex) return
        if (isModalOpen) {
            if (isVideoSlideIndex(selectedIndex) !== isVideoSlideIndex(targetIdx)) {
                setModalMediaTransitionDirection(targetIdx > selectedIndex ? 'next' : 'previous')
            }
            setIsModalGallerySwiper(!isVideoSlideIndex(targetIdx))
        }
        updateSelectedIndex(targetIdx)
        swiperRef?.current?.slideTo(targetIdx)
    }

    /**
     * Navigate and then restore focus on the arrow button that was activated.
     * This is necessary because some slide transitions cause a re-render that
     * would otherwise drop keyboard focus.
     */
    const navigateAndRefocus = (move, arrowRef) => {
        handleGalleryNavigation(move)
        requestAnimationFrame(() => arrowRef.current?.focus())
    }

    // Sync the modal swiper position whenever the parent changes modalInitValue.
    useEffect(() => {
        if (isModalOpen) {
            sliderRef.current?.slideTo(modalInitValue)
        }
    }, [modalInitValue, isModalOpen])

    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------

    return (
        <Flex
            direction={isModalOpen ? 'column-reverse' : {base: 'column-reverse', lg: 'row'}}
            gap={isModalOpen ? ['xsmall.base', 'xsmall.sm', 'xsmall.md', 'xsmall.lg'] : 0}
            w="100%"
            sx={
                !isModalOpen && bucketAnimationDirection
                    ? {
                          '@keyframes galleryBucketSlideFromRight': {
                              from: {opacity: 0, transform: 'translateX(24px)'},
                              to: {opacity: 1, transform: 'translateX(0)'},
                          },
                          '@keyframes galleryBucketSlideFromLeft': {
                              from: {opacity: 0, transform: 'translateX(-24px)'},
                              to: {opacity: 1, transform: 'translateX(0)'},
                          },
                          animation: `galleryBucketSlideFrom${
                              bucketAnimationDirection === 'left' ? 'Left' : 'Right'
                          } 260ms ease-out`,
                      }
                    : undefined
            }
        >
            {/* Thumbnail strip — only rendered inside the modal */}
            {isModalOpen && (
                <GallerySlider
                    ref={sliderRef}
                    key="gallery-slider-modal"
                    swiperRef={swiperRef}
                    scrollRef={scrollRef}
                    loadingStrategy={loadingStrategy}
                    videoUrl={videoUrl}
                    styles={styles}
                    scrollPosition={scrollPosition}
                    setScrollPosition={setScrollPosition}
                    selectedIndex={selectedIndex}
                    setSelectedIndex={updateSelectedIndex}
                    thumbnailImages={thumbnailImages}
                    handleGalleryNavigation={handleGalleryNavigation}
                    stopPropagation={stopPropagation}
                    openModal={openModal}
                    setIsModalGallerySwiper={setIsModalGallerySwiper}
                    isModalOpen={isModalOpen}
                    onBoundaryNavigate={onBoundaryNavigate}
                    modalInitValue={modalInitValue}
                />
            )}

            {/* Video hero stage — shown in modal when the active slide is a video */}
            {showVideoStageInModal && (
                <Flex
                    {...(isModalOpen ? styles.modalHeroStage : styles.heroImageStage)}
                    data-testid="hero-image-stage"
                >
                    <Flex
                        {...styles.heroImageGroup}
                        className="group"
                        onMouseEnter={() => setIsStageHovered(true)}
                        onMouseLeave={() => setIsStageHovered(false)}
                        onFocusCapture={() => setIsStageHovered(true)}
                        onBlurCapture={() => setIsStageHovered(false)}
                        data-testid="hero-image-group"
                        data-ab="gallery-exploded-slider"
                        data-cs-label={
                            !isModalOpen ? 'gallery-collapsed-slider' : 'gallery-exploded-slider'
                        }
                    >
                        <Flex
                            {...styles.heroLeftRightArrowsContainer}
                            {...styles.heroLeftRightArrowsContainerPrev}
                            opacity={1}
                            className="gallery-nav-arrow"
                            ref={prevArrowRef}
                            data-testid="swiper-button-prev"
                            tabIndex={0}
                            role="button"
                            aria-label={intl.formatMessage(messages.previousImage)}
                            onClick={() => navigateAndRefocus(false, prevArrowRef)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    navigateAndRefocus(false, prevArrowRef)
                                }
                            }}
                        >
                            <Flex
                                {...styles.heroLeftRightArrows}
                                {...handleChevronStyles(
                                    'horizontal',
                                    slideCount,
                                    selectedIndex,
                                    prevArrowBoundary,
                                )}
                            >
                                <GalleryChevronLeftIcon
                                    marginRight={remCalc(2)}
                                    {...styles.heroLeftRightArrows}
                                />
                            </Flex>
                        </Flex>

                        <Flex
                            data-testid="image-gallery-video"
                            {...styles.videoHero}
                            {...styles.videoHeroGallery}
                            sx={modalMediaTransitionSx}
                            onAnimationEnd={() => setModalMediaTransitionDirection(null)}
                        >
                            <YoutubeIframe title={video?.title} videoSrc={video?.videolink} />
                        </Flex>

                        <Flex
                            {...styles.heroLeftRightArrowsContainer}
                            {...styles.heroLeftRightArrowsContainerNext}
                            opacity={1}
                            className="gallery-nav-arrow"
                            ref={nextArrowRef}
                            data-testid="swiper-button-next"
                            tabIndex={0}
                            role="button"
                            aria-label={intl.formatMessage(messages.nextImage)}
                            onClick={() => navigateAndRefocus(true, nextArrowRef)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    navigateAndRefocus(true, nextArrowRef)
                                }
                            }}
                        >
                            <Flex
                                {...styles.heroLeftRightArrows}
                                {...handleChevronStyles(
                                    'horizontal',
                                    slideCount,
                                    selectedIndex,
                                    nextArrowBoundary,
                                )}
                            >
                                <GalleryChevronRightIcon
                                    marginLeft={remCalc(2)}
                                    {...styles.heroLeftRightArrows}
                                />
                            </Flex>
                        </Flex>
                    </Flex>
                </Flex>
            )}

            {/* Main hero stage — image or bodyContent placeholder */}
            {(isModalGallerySwiper || !isModalOpen) && (heroImage || bodyContent) && (
                <Flex
                    {...(isModalOpen ? styles.modalHeroStage : styles.heroImageStage)}
                    id={!isModalOpen ? panelId : undefined}
                    role={!isModalOpen && tabsContent ? 'tabpanel' : undefined}
                    aria-labelledby={!isModalOpen && tabsContent ? activeTabId : undefined}
                    data-testid="hero-image-stage"
                >
                    <Flex
                        {...styles.heroImageGroup}
                        className="group"
                        onMouseEnter={() => setIsStageHovered(true)}
                        onMouseLeave={() => setIsStageHovered(false)}
                        onFocusCapture={() => setIsStageHovered(true)}
                        onBlurCapture={() => setIsStageHovered(false)}
                        data-testid="hero-image-group"
                        data-ab={
                            !isModalOpen ? 'gallery-collapsed-slider' : 'gallery-exploded-slider'
                        }
                        // the whole stage is the single tab stop for opening the full-screen gallery; arrows/dots have their own
                        // skip this on the video slide so a click only starts playback instead of also opening the modal
                        {...(!isModalOpen && heroImage && !isVideoSlide
                            ? {
                                  tabIndex: 0,
                                  role: 'button',
                                  'aria-label': intl.formatMessage(messages.openGallery),
                                  onClick: () => {
                                      setIsModalGallerySwiper(true)
                                      openModal(selectedIndex)
                                  },
                                  onKeyDown: (e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                          e.preventDefault()
                                          setIsModalGallerySwiper(true)
                                          openModal(selectedIndex)
                                      }
                                  },
                              }
                            : {})}
                    >
                        {/* Previous arrow */}
                        {heroImage && (
                            <Flex
                                {...styles.heroLeftRightArrowsContainer}
                                {...styles.heroLeftRightArrowsContainerPrev}
                                opacity={isModalOpen ? 1 : {base: 1, lg: isStageHovered ? 1 : 0}}
                                className="gallery-nav-arrow"
                                ref={prevArrowRef}
                                data-testid="swiper-button-prev"
                                tabIndex={0}
                                role="button"
                                aria-label={intl.formatMessage(messages.previousImage)}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    navigateAndRefocus(false, prevArrowRef)
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        navigateAndRefocus(false, prevArrowRef)
                                    }
                                }}
                            >
                                <Flex
                                    {...styles.heroLeftRightArrows}
                                    {...handleChevronStyles(
                                        'horizontal',
                                        horizontalArrowItemsCount,
                                        selectedIndex,
                                        prevArrowBoundary,
                                    )}
                                >
                                    <GalleryChevronLeftIcon
                                        marginRight={remCalc(2)}
                                        {...styles.heroLeftRightArrows}
                                    />
                                </Flex>
                            </Flex>
                        )}

                        {/* Hero image / swiper */}
                        {heroImage && (
                            <Flex
                                width="100%"
                                height="100%"
                                sx={modalMediaTransitionSx}
                                onAnimationEnd={() => setModalMediaTransitionDirection(null)}
                            >
                                <ProductDetailGallery
                                    heroImageGroup={heroImageGroup}
                                    loadingStrategy={loadingStrategy}
                                    openModal={openModal}
                                    videoUrl={videoUrl}
                                    handleGalleryNavigation={handleGalleryNavigation}
                                    selectedIndex={selectedIndex}
                                    swiperRef={swiperRef}
                                    styles={styles}
                                    variant={variant}
                                    setIsModalGallerySwiper={setIsModalGallerySwiper}
                                    setSelectedIndex={updateSelectedIndex}
                                    isModalOpen={isModalOpen}
                                    onBoundaryNavigate={onBoundaryNavigate}
                                    subModelIdentifier={subModelIdentifier}
                                />
                            </Flex>
                        )}

                        {/* Optional body content slot (used when there is no hero image) */}
                        {!heroImage && bodyContent && bodyContent}

                        {/* Next arrow */}
                        {heroImage && (
                            <Flex
                                {...styles.heroLeftRightArrowsContainer}
                                {...styles.heroLeftRightArrowsContainerNext}
                                opacity={isModalOpen ? 1 : {base: 1, lg: isStageHovered ? 1 : 0}}
                                className="gallery-nav-arrow"
                                ref={nextArrowRef}
                                data-testid="swiper-button-next"
                                tabIndex={0}
                                role="button"
                                aria-label={intl.formatMessage(messages.nextImage)}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    navigateAndRefocus(true, nextArrowRef)
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        navigateAndRefocus(true, nextArrowRef)
                                    }
                                }}
                            >
                                <Flex
                                    {...styles.heroLeftRightArrows}
                                    {...handleChevronStyles(
                                        'horizontal',
                                        horizontalArrowItemsCount,
                                        selectedIndex,
                                        nextArrowBoundary,
                                    )}
                                >
                                    <GalleryChevronRightIcon
                                        marginLeft={remCalc(2)}
                                        {...styles.heroLeftRightArrows}
                                    />
                                </Flex>
                            </Flex>
                        )}

                        {/* Pagination dots + bucket tab-list (in-page only) */}
                        {!isModalOpen && (slideCount > 0 || tabsContent) && (
                            <Flex {...styles.dotsAndTabsContainer}>
                                {paginationSlideCount > 0 && (
                                    <Flex {...styles.slideIndicatorWrapper}>
                                        {new Array(paginationSlideCount)
                                            .fill(null)
                                            .map((_, index) => {
                                                // Translate the global dot index into a local slide index.
                                                const localIdx = index - paginationOffset
                                                const isLocalSlide =
                                                    localIdx >= 0 && localIdx < slideCount
                                                // A dot is interactive either because it maps to a local
                                                // slide or because the parent provides a jump callback.
                                                const isInteractive =
                                                    isLocalSlide || Boolean(onJumpToIndex)
                                                const isActive = index === paginationActiveIndex

                                                const activate = () =>
                                                    isLocalSlide
                                                        ? goToSlide(localIdx)
                                                        : onJumpToIndex?.(index)

                                                return (
                                                    <Flex
                                                        key={`slide-indicator-${index}`}
                                                        as={isInteractive ? 'button' : 'div'}
                                                        type={isInteractive ? 'button' : undefined}
                                                        {...styles.slideIndicator}
                                                        {...(isActive
                                                            ? styles.slideIndicatorActive
                                                            : styles.slideIndicatorInactive)}
                                                        tabIndex={isInteractive ? 0 : -1}
                                                        aria-hidden={!isInteractive}
                                                        aria-current={isActive ? 'true' : undefined}
                                                        aria-label={
                                                            isInteractive
                                                                ? intl.formatMessage(
                                                                      messages.goToImage,
                                                                      {index: index + 1},
                                                                  )
                                                                : undefined
                                                        }
                                                        onClick={
                                                            isInteractive
                                                                ? (e) => {
                                                                      e.stopPropagation()
                                                                      activate()
                                                                  }
                                                                : undefined
                                                        }
                                                        onKeyDown={
                                                            isInteractive
                                                                ? (e) => {
                                                                      if (
                                                                          e.key === 'Enter' ||
                                                                          e.key === ' '
                                                                      ) {
                                                                          e.preventDefault()
                                                                          e.stopPropagation()
                                                                          activate()
                                                                      }
                                                                  }
                                                                : undefined
                                                        }
                                                        data-testid={
                                                            isActive
                                                                ? 'slide-indicator-active'
                                                                : 'slide-indicator'
                                                        }
                                                    />
                                                )
                                            })}
                                    </Flex>
                                )}

                                {tabsContent}
                            </Flex>
                        )}
                    </Flex>
                </Flex>
            )}
        </Flex>
    )
}

GalleryBody.propTypes = {
    /** All image groups for the product (used to resolve LARGE and SMALL view types). */
    imageGroups: PropTypes.array,
    /** Currently selected variation attributes. */
    selectedVariationAttributes: PropTypes.object,
    /** When true images are loaded lazily. */
    lazy: PropTypes.bool,
    /** Callback to open the full-screen modal at a given slide index. */
    openModal: PropTypes.func,
    /** Chakra variant token forwarded to useMultiStyleConfig. */
    variant: PropTypes.string,
    /** JSON string describing intro / YouTube videos. */
    videoUrl: PropTypes.string,
    setIsModalGallerySwiper: PropTypes.func,
    isModalGallerySwiper: PropTypes.bool,
    /** True when rendered inside the full-screen modal. */
    isModalOpen: PropTypes.bool,
    /** Slide index the modal should open at (controlled by parent). */
    modalInitValue: PropTypes.number,
    /** Flat array of extra images appended after the standard imageGroups images. */
    powerImages: PropTypes.array,
    subModelIdentifier: PropTypes.string,
    /**
     * Controlled slide index.  When provided together with onSelectedIndexChange
     * the parent owns the current position; otherwise the component is uncontrolled.
     */
    selectedIndex: PropTypes.number,
    /** Called whenever the active slide changes so the parent can stay in sync. */
    onSelectedIndexChange: PropTypes.func,
    /** Starting slide used only in uncontrolled mode. */
    initialSelectedIndex: PropTypes.number,
    /**
     * Called when navigation reaches the first or last slide.
     * Return true to signal that the boundary was handled (e.g. bucket changed).
     */
    onBoundaryNavigate: PropTypes.func,
    /** Called when a pagination dot outside the current bucket is clicked. */
    onJumpToIndex: PropTypes.func,
    bucketAnimationDirection: PropTypes.oneOf(['left', 'right']),
    /** Total number of dots across all buckets (for the aggregated pagination strip). */
    paginationTotal: PropTypes.number,
    /** Index offset of the first dot belonging to this bucket in the aggregated strip. */
    paginationOffset: PropTypes.number,
    /** Rendered inside the dots row — intended for the bucket tab-list. */
    tabsContent: PropTypes.node,
    /** Rendered in the hero area when no heroImage is available. */
    bodyContent: PropTypes.node,
    panelId: PropTypes.string,
    activeTabId: PropTypes.string,
}

export default GalleryBody
